import * as React from "react";
/**
 * React error boundary component that catches render errors in its children
 * and displays a fallback UI. Supports both static fallback and render function
 * fallback with error details and reset capability.
 *
 * NOTE: This must be a class component. React requires getDerivedStateFromError
 * and componentDidCatch to be class methods — there is no functional/hook equivalent
 * (as of React 19). Consumers still use this declaratively via JSX props; the class
 * implementation is an internal detail.
 */ export class ErrorBoundary extends React.Component {
    // React can deliver any thrown value (including null, undefined, strings, numbers),
    // not just Error instances. We coerce to Error here so downstream consumers always
    // receive a proper Error object with .name and .message.
    static getDerivedStateFromError(error) {
        const wrappedError = error instanceof Error ? error : new Error(String(error ?? "Unknown error"));
        return {
            hasError: true,
            error: wrappedError
        };
    }
    componentDidCatch(error, errorInfo) {
        const wrappedError = error instanceof Error ? error : new Error(String(error ?? "Unknown error"));
        this.props.onError?.(wrappedError, errorInfo, this.props.name);
    }
    render() {
        // Gate on hasError alone — not this.state.error — because getDerivedStateFromError
        // always wraps to Error, but even if it didn't, we must show the fallback when
        // hasError is true to avoid re-rendering children that already threw.
        if (this.state.hasError) {
            const { fallback } = this.props;
            // error is guaranteed non-null here because getDerivedStateFromError always wraps to Error
            const error = this.state.error;
            if (typeof fallback === "function") {
                return fallback(error, this.resetErrorBoundary);
            }
            return fallback;
        }
        return this.props.children;
    }
    constructor(props){
        super(props), /**
     * Resets the error boundary state, allowing children to re-render.
     * Pass this to a retry button in the fallback UI.
     */ this.resetErrorBoundary = ()=>{
            this.setState({
                hasError: false,
                error: null
            });
        };
        this.state = {
            hasError: false,
            error: null
        };
    }
}
